// Contracts — kids faceval (face-duplicate validator, #34). Anti-fraud: don't
// film the same child twice. ArcFace runs on a Mac mini reached via a PULL queue
// (the mini polls the worker; no inbound tunnel). These schemas cover the three
// hops: browser→worker (enqueue a face), worker→browser (poll the verdict), and
// mini→worker (post the computed verdict). Threshold + embedding live on the mini.
import { z } from 'zod';

// A candidate the submitted face resembles ≥ threshold (cosine 0.45). Either a hit
// in the VIDEO base (an already-filmed child → pid/mod/date) or the INTAKE base
// ('tg' — a face added during a previous validation → who). thumbB64 is an optional
// small JPEG (base64, NO `data:` prefix) so the UI can show «похож на кого».
export const KidsFacevalCandidateSchema = z.object({
  src: z.enum(['video', 'tg']),
  sim: z.number(),
  pid: z.string().nullish(),
  mod: z.string().nullish(),
  date: z.string().nullish(),
  who: z.string().nullish(),
  thumbB64: z.string().nullish(),
});
export type KidsFacevalCandidate = z.infer<typeof KidsFacevalCandidateSchema>;

// The validator's verdict. `duplicate` = at least one candidate over threshold
// (a likely re-shoot → gate blocks); `clear` = a face was found but no match;
// `no_face` = no detectable face (the UI asks to retake the photo).
export const KidsFacevalVerdictSchema = z.object({
  faceFound: z.boolean(),
  verdict: z.enum(['duplicate', 'clear', 'no_face']),
  candidates: z.array(KidsFacevalCandidateSchema).default([]),
});
export type KidsFacevalVerdict = z.infer<typeof KidsFacevalVerdictSchema>;

// ── browser → worker: enqueue a face for validation (downscaled JPEG dataURL).
export const KidsFacevalValidateReqSchema = z.object({
  photo: z.string().min(1), // data:image/jpeg;base64,…
});
export type KidsFacevalValidateReq = z.infer<typeof KidsFacevalValidateReqSchema>;

export const KidsFacevalValidateResSchema = z.object({
  jobId: z.string(),
  status: z.literal('pending'),
});
export type KidsFacevalValidateRes = z.infer<typeof KidsFacevalValidateResSchema>;

// ── worker → browser (poll): job status + verdict once done.
export const KidsFacevalResultResSchema = z.object({
  status: z.enum(['pending', 'processing', 'done', 'error']),
  verdict: KidsFacevalVerdictSchema.nullable(),
});
export type KidsFacevalResultRes = z.infer<typeof KidsFacevalResultResSchema>;

// ── mini → worker: post the computed verdict for a claimed job.
export const KidsFacevalSubmitReqSchema = z.object({
  jobId: z.string().min(1),
  verdict: KidsFacevalVerdictSchema,
});
export type KidsFacevalSubmitReq = z.infer<typeof KidsFacevalSubmitReqSchema>;

// ── browser → worker: register a validated face into the intake base (facedb_tg).
// References the original validate job — its photo is reused from R2 (no re-upload).
// `who` = human label (participant name / PID) stored with the added face.
export const KidsFacevalAddReqSchema = z.object({
  jobId: z.string().min(1),
  who: z.string().max(200).optional(),
});
export type KidsFacevalAddReq = z.infer<typeof KidsFacevalAddReqSchema>;

// ── mini → worker: report the outcome of an 'add' job.
export const KidsFacevalAddResultReqSchema = z.object({
  jobId: z.string().min(1),
  ok: z.boolean(),
  pid: z.string().nullish(),
});
export type KidsFacevalAddResultReq = z.infer<typeof KidsFacevalAddResultReqSchema>;

// ── Фейс-метчер Отгрузки (B12, Tim 20.07): «Автоотгрузка — метчинг по лицу».
// Кадр из каждого выбранного видео сравнивается ТОЧЕЧНО с фото кандидатов дня
// (children.photo_key), не с общей базой. Один job на видео; payload джобы несёт
// [{childId, photoKey}] — без имён/PII.

// browser → worker: enqueue match jobs (один кадр на видео).
export const KidsFacevalMatchReqSchema = z.object({
  frames: z
    .array(
      z.object({
        ref: z.string().min(1).max(300), // client-side id (имя файла) для корреляции
        photo: z.string().min(1).max(2_000_000), // JPEG dataURL кадра
      }),
    )
    .min(1)
    .max(60),
  candidateChildIds: z.array(z.string().min(1)).min(1).max(200),
});
export type KidsFacevalMatchReq = z.infer<typeof KidsFacevalMatchReqSchema>;

export const KidsFacevalMatchResSchema = z.object({
  jobs: z.array(z.object({ ref: z.string(), jobId: z.string() })),
  // Кандидаты без фото карточки метчиться не могут — UI честно предупреждает.
  candidatesWithPhoto: z.number().int().nonnegative(),
  candidatesTotal: z.number().int().nonnegative(),
});
export type KidsFacevalMatchRes = z.infer<typeof KidsFacevalMatchResSchema>;

// mini → worker: computed match for a claimed 'match' job. best/second — топ-2
// кандидата по cosine (second нужен фронту для margin-порога).
export const KidsFacevalMatchResultReqSchema = z.object({
  jobId: z.string().min(1),
  faceFound: z.boolean(),
  best: z.object({ childId: z.string(), sim: z.number() }).nullish(),
  second: z.object({ childId: z.string(), sim: z.number() }).nullish(),
});
export type KidsFacevalMatchResultReq = z.infer<typeof KidsFacevalMatchResultReqSchema>;
